'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: string;
  name: string;
  role: string;
  image: string;
  alt: string;
  rating: number;
  comment: string;
  date: string;
}

interface CustomerTestimonialsProps {
  className?: string;
}

const CustomerTestimonials = ({ className = '' }: CustomerTestimonialsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
    {
      id: '1',
      name: 'Sumit Ghosh',
      role: 'Regular Customer',
      image: '/assets/people/1.jpeg',
      alt: 'Professional woman with long brown hair smiling warmly in casual blue sweater',
      rating: 5,
      comment:
        'Absolutely amazing food! The delivery was super fast and everything arrived hot and fresh. The flavors are authentic and portions are generous. Freshlymade has become my go-to for weeknight dinners.',
      date: '2026-01-20',
    },
    {
      id: '2',
      name: 'Sumit Tomar',
      role: 'Food Enthusiast',
      image: '/assets/people/2.jpeg',
      alt: 'Young Asian man with short black hair in white shirt smiling confidently outdoors',
      rating: 5,
      comment:
        'Best food delivery service in town! The quality is consistently excellent and the prices are very reasonable. I love how they use fresh ingredients and you can really taste the difference. Highly recommended!',
      date: '2026-01-18',
    },
    {
      id: '3',
      name: 'Sonu Singh',
      role: 'Business Professional',
      image: '/assets/people/3.jpeg',
      alt: 'Hispanic woman with curly dark hair in professional attire smiling brightly',
      rating: 5,
      comment:
        'As someone who works long hours, Freshlymade is a lifesaver! The app is easy to use, delivery is always on time, and the food quality never disappoints. Their customer service is also top-notch.',
      date: '2026-01-15',
    },
  ];

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  const currentTestimonial = testimonials[currentIndex];

  if (!isHydrated) {
    return (
      <section className={`bg-background ${className}`}>
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-heading text-3xl font-bold text-foreground sm:text-4xl">
              Customer Testimonials
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">
              Loading testimonials...
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`bg-background ${className}`}>
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center">
          <h2 className="font-heading text-3xl font-bold text-foreground sm:text-4xl">
            Customer Testimonials
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">
            Don't just take our word for it. Here's what our satisfied customers have to say about
            their Freshlymade experience.
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative mx-auto mt-12 max-w-4xl">
          <div className="overflow-hidden rounded-xl bg-card p-8 shadow-lg sm:p-12">
            {/* Quote Icon */}
            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <Icon name="ChatBubbleLeftRightIcon" size={32} className="text-primary" />
              </div>
            </div>

            {/* Rating */}
            <div className="mt-6 flex justify-center space-x-1">
              {[...Array(currentTestimonial.rating)].map((_, index) => (
                <Icon
                  key={index}
                  name="StarIcon"
                  size={24}
                  className="text-warning"
                  variant="solid"
                />
              ))}
            </div>

            {/* Comment */}
            <blockquote className="mt-6 text-center text-lg leading-relaxed text-foreground">
              "{currentTestimonial.comment}"
            </blockquote>

            {/* Customer Info */}
            <div className="mt-8 flex flex-col items-center">
              <div className="h-16 w-16 overflow-hidden rounded-full">
                <AppImage
                  src={currentTestimonial.image}
                  alt={currentTestimonial.alt}
                  className="h-full w-full object-cover"
                />
              </div>
              <p className="mt-4 font-heading text-lg font-semibold text-foreground">
                {currentTestimonial.name}
              </p>
              <p className="text-caption mt-1 text-muted-foreground">{currentTestimonial.role}</p>
            </div>

            {/* Navigation Buttons */}
            <div className="mt-8 flex items-center justify-center space-x-4">
              <button
                type="button"
                onClick={handlePrevious}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background text-foreground transition-smooth hover:bg-muted focus-ring"
                aria-label="Previous testimonial"
              >
                <Icon name="ChevronLeftIcon" size={20} />
              </button>
              <div className="flex space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setCurrentIndex(index)}
                    className={`h-2 w-2 rounded-full transition-smooth focus-ring ${
                      index === currentIndex ? 'bg-primary' : 'bg-border'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={handleNext}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background text-foreground transition-smooth hover:bg-muted focus-ring"
                aria-label="Next testimonial"
              >
                <Icon name="ChevronRightIcon" size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Trust Metrics */}
        <div className="mt-16 grid gap-8 sm:grid-cols-3">
          <div className="text-center">
            <p className="text-data text-4xl font-bold text-primary">98%</p>
            <p className="mt-2 text-sm text-muted-foreground">Customer Satisfaction</p>
          </div>
          <div className="text-center">
            <p className="text-data text-4xl font-bold text-primary">50K+</p>
            <p className="mt-2 text-sm text-muted-foreground">Orders Delivered</p>
          </div>
          <div className="text-center">
            <p className="text-data text-4xl font-bold text-primary">4/5</p>
            <p className="mt-2 text-sm text-muted-foreground">Average Rating</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomerTestimonials;
